import { css, Button, Text, Toast, tokens } from "@balabalabom/ui";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  miniBookService,
  resolveImageUrl,
  parseEditJson,
  normalizePageRotation,
  stepPageRotation,
  type ExportOptionsInput,
  type PaperSize,
  type PageLabelPosition,
  type PageRotationDeg,
  type EditJson,
} from "../lib/miniBookService";

const ORANGE = "#e89b5f";

function formatPageLabel(renderLabel: string | undefined, logicalPage: number): string {
  if (!renderLabel) return `Page ${logicalPage}`;
  if (renderLabel === "FC") return "Front Cover";
  if (renderLabel === "BC") return "Back Cover";
  if (/^\d+$/.test(renderLabel)) return `Page ${renderLabel}`;
  return renderLabel;
}
const ORANGE_DARK = "#d88a4f";
const CARD_BG = "#f8f6f2";
const BORDER = "#e8e4dc";

interface CreateState {
  exportDefaults?: {
    paperSize?: PaperSize;
    pageWidthInches?: number;
    pageHeightInches?: number;
    pageLabelPosition?: PageLabelPosition;
    showInstructions?: boolean;
    instructionBackgroundPath?: string;
    includeDetailInstructions?: boolean;
    foldLineColorHex?: string;
    outlineLineColorHex?: string;
    cutLineColorHex?: string;
  };
}

function editJsonFromProject(
  pageEdits: { logicalPage: number; editJson: string; backgroundAssetId: number }[],
  logicalPage: number,
): { edit: EditJson; backgroundAssetId?: number } {
  const row = pageEdits.find((e) => e.logicalPage === logicalPage);
  return {
    edit: parseEditJson(row?.editJson),
    backgroundAssetId: row?.backgroundAssetId ? row.backgroundAssetId : undefined,
  };
}

export function MiniBookProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const createState = location.state as CreateState | null;
  const pid = projectId ? Number(projectId) : 0;

  const [project, setProject] = useState<Awaited<ReturnType<typeof miniBookService.getProject>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [uploadingPage, setUploadingPage] = useState<number | null>(null);
  const [rotatingPage, setRotatingPage] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pageImageUrls, setPageImageUrls] = useState<Record<number, string>>({});
  const [pageRotations, setPageRotations] = useState<Record<number, PageRotationDeg>>({});
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const exportOptions: ExportOptionsInput = {
    paperSize: createState?.exportDefaults?.paperSize ?? "LETTER",
    pageWidthInches: createState?.exportDefaults?.pageWidthInches ?? 2.125,
    pageHeightInches: createState?.exportDefaults?.pageHeightInches ?? 5.5,
    pageLabelPosition: createState?.exportDefaults?.pageLabelPosition ?? "bottom-right",
    showInstructions: createState?.exportDefaults?.showInstructions ?? true,
    instructionBackgroundPath: createState?.exportDefaults?.instructionBackgroundPath,
    includeDetailInstructions: createState?.exportDefaults?.includeDetailInstructions ?? true,
  };

  const hydrateFromProject = (
    data: Awaited<ReturnType<typeof miniBookService.getProject>>,
  ) => {
    const urls: Record<number, string> = {};
    const rotations: Record<number, PageRotationDeg> = {};
    for (const edit of data.pageEdits) {
      const parsed = parseEditJson(edit.editJson);
      if (parsed.custom_image_url) {
        urls[edit.logicalPage] = resolveImageUrl(parsed.custom_image_url);
      }
      rotations[edit.logicalPage] = normalizePageRotation(parsed.rotation_deg);
    }
    setPageImageUrls(urls);
    setPageRotations(rotations);
  };

  useEffect(() => {
    if (!pid) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await miniBookService.getProject(pid);
        setProject(data);
        hydrateFromProject(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load project";
        setError(msg);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pid]);

  const selectedPages = project?.project?.selectedPagesList ?? [];
  const pageLayoutMap = new Map(
    (project?.pageLayout ?? []).map((p) => [p.logicalPage, p.renderLabel])
  );

  const handleUpload = async (logicalPage: number, file: File) => {
    if (!pid || !file) return;
    try {
      setUploadingPage(logicalPage);
      setUploadError(null);
      const result = await miniBookService.uploadPageImage(pid, logicalPage, file);
      const imageUrl =
        result?.url ??
        result?.imageUrl ??
        (result as { image_url?: string })?.image_url ??
        (result as { image?: { url?: string } })?.image?.url ??
        (result as { data?: { url?: string } })?.data?.url;
      const data = await miniBookService.getProject(pid);
      setProject(data);
      hydrateFromProject(data);
      if (typeof imageUrl === "string" && imageUrl) {
        setPageImageUrls((prev) => ({
          ...prev,
          [logicalPage]: resolveImageUrl(imageUrl),
        }));
      } else {
        setPageImageUrls((prev) => ({
          ...prev,
          [logicalPage]: URL.createObjectURL(file),
        }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      console.error(err);
    } finally {
      setUploadingPage(null);
      const input = fileInputRefs.current[logicalPage];
      if (input) input.value = "";
    }
  };

  const handleRotate = async (logicalPage: number, delta: 90 | -90) => {
    if (!pid || !project) return;
    const { edit, backgroundAssetId } = editJsonFromProject(project.pageEdits, logicalPage);
    if (!edit.custom_image_url && !pageImageUrls[logicalPage]) {
      setUploadError("Upload an image before rotating this page.");
      return;
    }
    const next = stepPageRotation(edit.rotation_deg ?? pageRotations[logicalPage] ?? 0, delta);
    const nextEdit: EditJson = {
      ...edit,
      text_blocks: edit.text_blocks ?? [],
      rotation_deg: next === 0 ? undefined : next,
    };
    // Keep 0 out of JSON to match omitempty, but UI still tracks 0.
    if (next === 0) {
      delete nextEdit.rotation_deg;
    } else {
      nextEdit.rotation_deg = next;
    }
    try {
      setRotatingPage(logicalPage);
      setUploadError(null);
      await miniBookService.updateProjectPage(
        pid,
        logicalPage,
        backgroundAssetId,
        JSON.stringify(nextEdit),
      );
      setPageRotations((prev) => ({ ...prev, [logicalPage]: next }));
      const data = await miniBookService.getProject(pid);
      setProject(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Rotate failed";
      setUploadError(msg);
      console.error(err);
    } finally {
      setRotatingPage(null);
    }
  };

  const handleExport = async () => {
    if (!pid) return;
    try {
      setExporting(true);
      setError(null);
      setExportSuccess(null);
      const { pdfData, statusMessage } = await miniBookService.exportProject(pid, exportOptions);
      if (!pdfData || pdfData.length === 0) {
        setError(statusMessage || "Export failed: no PDF data received");
        return;
      }
      const buffer = pdfData.buffer.slice(
        pdfData.byteOffset,
        pdfData.byteOffset + pdfData.byteLength
      ) as ArrayBuffer;
      const blob = new Blob([buffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setExportSuccess(statusMessage ? `PDF ready. ${statusMessage}` : "PDF ready! Opened in a new tab.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Export failed";
      setError(msg);
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: ORANGE })}>Loading project...</Text>
      </main>
    );
  }

  if (error && !project) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: "#ef4444", marginBottom: "1rem" })}>{error}</Text>
        <Button variant="primary" onClick={() => navigate("/minibook")} style={css({ backgroundColor: ORANGE })}>
          Back to Create
        </Button>
      </main>
    );
  }

  return (
    <main
      className="main-mobile"
      style={css({
        maxWidth: "80rem",
        margin: "0 auto",
        padding: "1.5rem",
        minHeight: "60vh",
        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(253, 224, 71, 0.12) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(253, 224, 71, 0.08) 0%, transparent 40%)`,
      })}
    >
      <div
        style={css({
          backgroundColor: CARD_BG,
          borderRadius: "1.25rem",
          border: `1px solid ${BORDER}`,
          padding: "2rem",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        })}
      >
        <h2 style={css({ fontSize: "1.5rem", fontWeight: 700, color: ORANGE, margin: 0, marginBottom: "0.5rem" })}>
          {project?.project?.name ?? "Mini Book"}
        </h2>
        <Text variant="body" style={css({ color: tokens.colors.neutral[600], marginBottom: "1.5rem" })}>
          Upload an image for each page, rotate if needed, then export your PDF.
        </Text>

        <div style={css({ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem" })}>
          {selectedPages.map((lp) => {
            const rotation = pageRotations[lp] ?? 0;
            const hasImage = Boolean(pageImageUrls[lp]);
            const busy = uploadingPage === lp || rotatingPage === lp;
            return (
              <div
                key={lp}
                style={css({
                  border: `1px solid ${BORDER}`,
                  borderRadius: "0.75rem",
                  padding: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.75rem",
                })}
              >
                <Text variant="body" style={css({ fontWeight: 600, color: tokens.colors.neutral[800] })}>
                  {formatPageLabel(pageLayoutMap.get(lp), lp)}
                </Text>
                {hasImage ? (
                  <div
                    style={css({
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: "0.5rem",
                      overflow: "hidden",
                      backgroundColor: "#f5f0e8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    })}
                  >
                    <img
                      src={pageImageUrls[lp]}
                      alt={`Page ${lp}`}
                      style={css({
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transform: `rotate(${rotation}deg)`,
                        transition: "transform 0.2s ease",
                      })}
                    />
                  </div>
                ) : null}
                <input
                  ref={(el) => { fileInputRefs.current[lp] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                  style={css({ display: "none" })}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(lp, f);
                  }}
                />
                <div style={css({ display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "center" })}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => fileInputRefs.current[lp]?.click()}
                    style={css({
                      borderColor: BORDER,
                      color: tokens.colors.neutral[700],
                    })}
                  >
                    {uploadingPage === lp ? "Uploading…" : hasImage ? "Replace" : "Upload image"}
                  </Button>
                  {hasImage ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => handleRotate(lp, -90)}
                        aria-label={`Rotate ${formatPageLabel(pageLayoutMap.get(lp), lp)} left`}
                        style={css({ borderColor: BORDER, color: tokens.colors.neutral[700] })}
                      >
                        Rotate left
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => handleRotate(lp, 90)}
                        aria-label={`Rotate ${formatPageLabel(pageLayoutMap.get(lp), lp)} right`}
                        style={css({ borderColor: BORDER, color: tokens.colors.neutral[700] })}
                      >
                        Rotate right
                      </Button>
                    </>
                  ) : null}
                </div>
                {hasImage && rotation !== 0 ? (
                  <Text variant="body" style={css({ color: tokens.colors.neutral[500], margin: 0, fontSize: "0.75rem" })}>
                    Rotated {rotation}°
                  </Text>
                ) : null}
              </div>
            );
          })}
        </div>

        {uploadError && (
          <Text variant="body" style={css({ color: "#ef4444", marginTop: "1rem" })}>
            {uploadError}
          </Text>
        )}

        <div style={css({ marginTop: "1.5rem", display: "flex", gap: "1rem", flexWrap: "wrap" })}>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={exporting}
            style={css({
              backgroundColor: ORANGE,
              "&:hover:not(:disabled)": { backgroundColor: ORANGE_DARK },
            })}
          >
            {exporting ? "Exporting…" : "Export PDF"}
          </Button>
          <Button variant="secondary" onClick={() => navigate("/minibook")} style={css({ borderColor: BORDER })}>
            ← Back to Create
          </Button>
        </div>
      </div>

      {error && (
        <Toast variant="error" message={error} onClose={() => setError(null)} />
      )}
      {exportSuccess && !error && (
        <Toast variant="success" message={exportSuccess} onClose={() => setExportSuccess(null)} />
      )}
    </main>
  );
}
